import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { api } from '../../lib/api';
import DeliverableRow from './DeliverableRow';
import EmptyState from './EmptyState';
import ConfirmDialog from '../admin/ConfirmDialog';
import MediaLinkDialog from '../admin/MediaLinkDialog';
import { DELIVERABLE_TYPE_META } from '../../lib/projects';

const TYPE_OPTIONS = Object.entries(DELIVERABLE_TYPE_META).map(([v, m]) => [v, m.label]);
// First type in DELIVERABLE_TYPE_META — used as the default for the "Add
// deliverable" dropdown so the React state always matches the rendered
// option. Previously defaulted to 'photography' which isn't in the enum,
// so submitting without touching the dropdown sent an invalid type.
const DEFAULT_NEW_TYPE = TYPE_OPTIONS[0]?.[0] || 'site_build';

// First entry from a deliverable's asset_urls_json (text column). Only used
// to seed the MediaLinkDialog's existingUrl when editing a media_capture row.
function firstAssetUrl(jsonText) {
  if (!jsonText) return '';
  try {
    const parsed = JSON.parse(jsonText);
    if (!Array.isArray(parsed)) return '';
    const first = parsed.find((x) => typeof x === 'string' && x.trim());
    return first || '';
  } catch { return ''; }
}

export default function DeliverableChecklist({ projectId, deliverables, onChange }) {
  const [showNew, setShowNew] = useState(false);
  const [type, setType] = useState(DEFAULT_NEW_TYPE);
  const [description, setDescription] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // mediaDialog: { deliverable, mode } where mode is 'complete' | 'edit'
  // Opens for media_capture rows only — both when first marking delivered
  // (URL optional) and when editing an existing or backfilling a missing URL.
  const [mediaDialog, setMediaDialog] = useState(null);

  const onStatus = async (d, target) => {
    try {
      const res = await api.put(`/api/admin/deliverables/${d.id}`, { status: target });
      onChange?.(deliverables.map((x) => x.id === d.id ? res.deliverable : x));
    } catch (err) {
      alert(err.message || 'Failed to update status');
    }
  };

  const onComplete = async (d) => {
    // For media_capture, route through the MediaLinkDialog so the user can
    // attach a Drive/media-library URL at completion time. Other deliverable
    // types complete immediately via the existing endpoint.
    if (d.type === 'media_capture') {
      setMediaDialog({ deliverable: d, mode: 'complete' });
      return;
    }
    try {
      const res = await api.post(`/api/admin/deliverables/${d.id}/complete`);
      onChange?.(deliverables.map((x) => x.id === d.id ? res.deliverable : x));
    } catch (err) {
      alert(err.message || 'Failed to complete');
    }
  };

  // Pencil button on already-delivered media_capture rows opens the dialog
  // in edit mode so the URL can be added (backfill), changed, or removed.
  const onEditMediaLink = (d) => {
    setMediaDialog({ deliverable: d, mode: 'edit' });
  };

  const submitMediaLink = async ({ deliverableId, mediaUrl, mode }) => {
    if (mode === 'complete') {
      const body = mediaUrl ? { assetUrls: [mediaUrl] } : null;
      const res = await api.post(`/api/admin/deliverables/${deliverableId}/complete`, body);
      onChange?.(deliverables.map((x) => x.id === deliverableId ? res.deliverable : x));
    } else {
      // edit/add/remove on already-delivered — PATCH avoids resetting completed_date.
      const assetUrlsJson = mediaUrl ? JSON.stringify([mediaUrl]) : null;
      const res = await api.patch(`/api/admin/deliverables/${deliverableId}`, { assetUrlsJson });
      onChange?.(deliverables.map((x) => x.id === deliverableId ? res.deliverable : x));
    }
    setMediaDialog(null);
  };

  const onDelete = (d) => { setDeleteTarget(d); setDeleteOpen(true); };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await api.delete(`/api/admin/deliverables/${deleteTarget.id}`);
    onChange?.(deliverables.filter((x) => x.id !== deleteTarget.id));
  };
  const create = async () => {
    try {
      const res = await api.post(`/api/admin/projects/${projectId}/deliverables`, {
        type, description: description.trim() || null,
      });
      onChange?.([...deliverables, res.deliverable]);
      setShowNew(false);
      setDescription('');
      setType(DEFAULT_NEW_TYPE);
    } catch (err) {
      alert(err.message || 'Create failed');
    }
  };

  return (
    <div className="space-y-2">
      {deliverables.length === 0 && !showNew ? (
        <EmptyState title="No deliverables yet" description="Seeded automatically for website projects." action={
          <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900"><Plus className="w-3 h-3"/>Add deliverable</button>
        } />
      ) : (
        <>
          {deliverables.map((d) => (
            <DeliverableRow
              key={d.id}
              deliverable={d}
              onStatus={onStatus}
              onComplete={onComplete}
              onDelete={onDelete}
              onEditMediaLink={d.type === 'media_capture' && d.status === 'delivered' ? onEditMediaLink : undefined}
            />
          ))}
          {showNew ? (
            <div className="p-3 rounded-xl border border-white/[0.08] bg-white/[0.02] space-y-2">
              <div className="flex items-center gap-2">
                <select value={type} onChange={(e) => setType(e.target.value)} className="bg-white/[0.03] border border-white/[0.08] rounded-lg px-2 py-1 text-[12px] text-white">
                  {TYPE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <input
                  className="flex-1 bg-white/[0.03] border border-white/[0.08] rounded-lg px-2 py-1 text-[12px] text-white"
                  placeholder="Optional description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setShowNew(false)} className="text-[11px] text-gray-500 hover:text-white">Cancel</button>
                <button onClick={create} className="px-2 py-1 rounded-lg text-[11px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900">Add</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-gray-400 hover:text-white hover:bg-white/[0.03]">
              <Plus className="w-3 h-3" /> Add deliverable
            </button>
          )}
        </>
      )}
      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
        title="Delete deliverable?"
        description={deleteTarget ? `Permanently removes the "${DELIVERABLE_TYPE_META[deleteTarget.type]?.label || deleteTarget.type}" deliverable.` : ''}
        confirmLabel="Delete"
        destructive
      />
      <MediaLinkDialog
        open={!!mediaDialog}
        mode={mediaDialog?.mode || 'complete'}
        deliverableId={mediaDialog?.deliverable?.id}
        existingUrl={firstAssetUrl(mediaDialog?.deliverable?.assetUrlsJson)}
        headerTitle="Media capture"
        subtitle={
          mediaDialog?.deliverable
            ? (mediaDialog.mode === 'edit' && mediaDialog.deliverable.completedDate
                ? `Completed ${String(mediaDialog.deliverable.completedDate).slice(0, 10)}`
                : (mediaDialog.deliverable.description || ''))
            : ''
        }
        onClose={() => setMediaDialog(null)}
        onSubmit={submitMediaLink}
      />
    </div>
  );
}
